import type React from 'react';
import { createBoundRender } from './createBoundRender';
import { getBindingMeta } from './bindingMetaRegistry';
import type { ComponentBindingMeta } from './types';
import type { TrackingMeta } from '../../components/Trackable/Trackable';

/**
 * Duck-typed shape of a Puck-style config. We purposefully do NOT
 * import `@puckeditor/core` types here so this package stays
 * independent of Puck version churn (plan Q4). DB-1.5 owns the
 * Puck-specific wiring when it integrates `applyBindings` into
 * `DslConfig.tsx`.
 */
export interface BoundConfig<
  TComponents extends Record<string, { render: React.ComponentType<never> }>,
> {
  readonly components: TComponents;
}

/**
 * Wrap every `components[name].render` in the supplied config with
 * {@link createBoundRender}. Pure — same input produces the same output.
 *
 * The enhancer is a **one-time config mutation** per dslConfig build
 * (plan Q2). Calling it twice on the same config is safe: each
 * component is only wrapped when its render does not already carry the
 * `__edu_bound` flag, so repeated `applyBindings` calls collapse to a
 * no-op after the first pass.
 *
 * `metaByComponent` lets consumers override `eventPropNames`,
 * `formKeyValueTransform`, or `staticProps` per component. Components
 * without an entry use the defaults.
 *
 * `trackingDefaultsByComponent` lets the DSL render path declare a
 * per-hen-ui-type tracking baseline (kind, fieldType, …) that
 * `createBoundRender` merges underneath any author-supplied
 * `_meta.tracking.*` slice. Realises EDUN-10692 AC #3 — DSL components
 * remain pure presentational; tracking semantics come from the
 * descriptor registry rather than per-instance authoring.
 *
 * Non-component keys on `config` (editor, root, extraOptions, …) pass
 * through unchanged — we only replace `components`.
 */
export function applyBindings<
  TConfig extends {
    components: Record<string, { render: React.ComponentType<never> }>;
  },
>(
  config: TConfig,
  metaByComponent?: Readonly<Record<string, ComponentBindingMeta>>,
  trackingDefaultsByComponent?: Readonly<Record<string, TrackingMeta>>,
): TConfig {
  const enhanced: Record<string, { render: React.ComponentType<never> }> = {};
  const names = Object.keys(config.components);

  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    const def = config.components[name];
    // Explicit consumer overrides win; otherwise fall back to the
    // central registry so components with default binding meta (e.g.
    // RankingCard's controlled-state descriptor) pick it up without the
    // caller having to spell it out.
    const meta = metaByComponent?.[name] ?? getBindingMeta(name);
    const trackingDefault = trackingDefaultsByComponent?.[name];
    const nextRender = createBoundRender(
      def.render as unknown as React.ComponentType<object>,
      meta,
      trackingDefault,
      name,
    ) as unknown as React.ComponentType<never>;
    // The repeater sees this wrapper, not the hen-ui component, while a
    // shell is pending. The authored DSL type is what decides loading props.
    (nextRender as { authoredDslType?: string }).authoredDslType = name;
    enhanced[name] = { ...def, render: nextRender };
  }

  return { ...config, components: enhanced };
}
